import PropTypes from "prop-types";
import React, { useRef, useState } from "react";

import styled from "styled-components/macro";

import {
  useCanContactUs,
  useHasAnyElevatedPermissions,
} from "Flocknote/stateHooks";

import { object, string } from "yup";

import FnrButton from "UI/FnrButton3";
import FnrInput3 from "UI/FnrInput3";
import PhoneInput from "UI/FnrInput3/Inputs/PhoneInput";
import FnrSimpleError from "UI/FnrSimpleError";

import { useGetBackgroundColorData } from "../../API/queryHooks";
import { useRecaptcha } from "../hooks/useRecaptcha";

import { SignUpButton } from "./Components/SignupButton";
import LoginButton from "./LoginButton";

const GetInvolvedForm = ({
  userData,
  isLoggedIn,
  isRecaptchaRequired,
  tenDlcNotice,
  tenDlcCheckbox,
  showPhone,
}) => {
  const hasAnyElevatedPermissions = useHasAnyElevatedPermissions();
  const [canContactUs] = useCanContactUs();
  const { accentColor } = useGetBackgroundColorData();

  const formContainerRef = useRef(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [formData, setFormData] = useState({
    fname: "",
    lname: "",
    email: "",
    mobile_phone: "",
  });

  const validationSchema = object({
    fname: string().trim().default(""),
    lname: string().trim().default(""),
    email: string()
      .trim()
      .email("Please enter a valid email address")
      .default(""),
    mobile_phone: string().trim().default(""),
  }).test(
    "at-least-one-field",
    "Please enter your information",
    function (values) {
      return Object.values(values).some((value) => value?.trim());
    }
  );

  // Keep a reference to the latest form data to avoid React closure issues
  const currentFormDataRef = useRef(formData);
  currentFormDataRef.current = formData;

  const { recaptchaRef, executeRecaptcha } = useRecaptcha({
    // Currently this is pointed to Oscars Google reCAPTCHA site key
    siteKey: "6LeQYBsUAAAAABal-CyBCQohk4FmWCU2VIMoGR7U",
    enabled: isRecaptchaRequired,
    size: "invisible",
    theme: "dark",
    onSuccess: (recaptchaToken) => {
      handleRecaptchaSuccess(recaptchaToken);
    },
    onError: () => {
      console.error("reCAPTCHA verification failed");
      setRecaptchaError(true);
      setIsSubmitting(false);
    },
  });

  const handleRecaptchaSuccess = (recaptchaToken) => {
    const currentFormData = currentFormDataRef.current;
    submitForm(currentFormData, recaptchaToken);
  };

  const submitForm = async (formDataToSubmit, recaptchaToken = null) => {
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("fname", formDataToSubmit.fname);
      formDataToSend.append("lname", formDataToSubmit.lname);
      formDataToSend.append("email", formDataToSubmit.email);
      formDataToSend.append("mobile_phone", formDataToSubmit.mobile_phone);

      if (recaptchaToken) {
        formDataToSend.append("g-recaptcha-response", recaptchaToken);
      }

      const response = await fetch("/group/addToGroupFromOutside/everyone", {
        method: "POST",
        body: formDataToSend,
      });

      if (response.ok) {
        window.location.href = response.url;
      } else {
        console.error("Form submission failed:", response.status);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      if (recaptchaToken && window.grecaptcha && recaptchaRef.current) {
        window.grecaptcha.reset(recaptchaRef.current);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((previousData) => ({
      ...previousData,
      [name]: value,
    }));

    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError("");
    }
  };

  const handleFocus = (e) => {
    e.preventDefault();

    if (window.innerWidth < 600 && formContainerRef.current) {
      // Scroll so the top of the form is at the top of the viewport
      formContainerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setValidationError("");

    // validate with yup that there is at least one field filled out
    try {
      await validationSchema.validate(formData, { abortEarly: false });
    } catch (error) {
      console.error("Validation error:", error);
      setValidationError(error.message || "Please fill out at least one field");
      setIsSubmitting(false);
      return;
    }

    if (isRecaptchaRequired) {
      const wasRecaptchaExecuted = executeRecaptcha();

      if (!wasRecaptchaExecuted || recaptchaError) {
        await submitForm(formData);
      }
    } else {
      await submitForm(formData);
    }
  };

  return (
    <FormContainer ref={formContainerRef}>
      {isLoggedIn ? (
        <WelcomeContainer>
          <WelcomeMessage>
            <WelcomeGreeting>
              {userData.fname
                ? `Welcome back, ${userData.fname}`
                : `Welcome back`}
            </WelcomeGreeting>
            {hasAnyElevatedPermissions ? (
              <AdminWrapper>
                <AdminText>
                  Ready to lead your flock? Visit the Admin Dashboard to send
                  notes, manage groups, and&nbsp;more.
                </AdminText>
                <FnrButton
                  borderRadius={9999}
                  color={accentColor}
                  onClick={() => (window.location.href = "/dashboard")}
                >
                  <ButtonIcon className="fad fa-user-shield" /> Admin Dashboard
                </FnrButton>
              </AdminWrapper>
            ) : (
              <AdminWrapper>
                <AdminText>
                  Visit your profile to view or update your contact information,
                  manage your group subscriptions, and more.
                </AdminText>
                <FnrButton
                  borderRadius={9999}
                  color={accentColor}
                  onClick={() =>
                    (window.location.href = `/user/${userData.userID}`)
                  }
                >
                  <ButtonIcon className="fad fa-user" /> My Profile
                </FnrButton>
              </AdminWrapper>
            )}
          </WelcomeMessage>
        </WelcomeContainer>
      ) : (
        <>
          <FormTitle>Get Involved</FormTitle>
          <Form onSubmit={handleSubmit} onFocus={handleFocus}>
            <NameInputs>
              <FnrInput3
                Component={CustomStyledInput}
                id="fname"
                name="fname"
                value={formData.fname}
                onChange={handleInputChange}
                label="First name"
              />
              <FnrInput3
                Component={CustomStyledInput}
                id="lname"
                name="lname"
                value={formData.lname}
                onChange={handleInputChange}
                label="Last name"
              />
            </NameInputs>

            <FnrInput3
              Component={CustomStyledInput}
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              label="Email"
            />

            {showPhone && (
              <FnrInput3
                Component={PhoneInput}
                id="mobile_phone"
                name="mobile_phone"
                value={formData.mobile_phone}
                onChange={handleInputChange}
                label="Phone number"
              />
            )}

            <Buttons>
              {canContactUs && <LoginButton />}

              <FnrButton
                type="submit"
                borderRadius={9999}
                color={accentColor}
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Sign Me Up
              </FnrButton>
            </Buttons>

            <FnrSimpleError show={!!validationError} marginY={0} marginX={0}>
              {validationError}
            </FnrSimpleError>

            <BottomText>
              {tenDlcNotice && (
                <TenDlcNotice
                  dangerouslySetInnerHTML={{ __html: tenDlcNotice }}
                />
              )}

              {tenDlcCheckbox && (
                <TenDlcCheckbox>
                  <input type="checkbox" />
                  {tenDlcCheckbox}
                </TenDlcCheckbox>
              )}
            </BottomText>
          </Form>
        </>
      )}
      {/* Hidden reCAPTCHA container badge */}
      {isRecaptchaRequired && (
        <div ref={recaptchaRef} style={{ visibility: "hidden" }} />
      )}
    </FormContainer>
  );
};

GetInvolvedForm.propTypes = {
  userData: PropTypes.object.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  isRecaptchaRequired: PropTypes.bool,
  tenDlcNotice: PropTypes.string,
  tenDlcCheckbox: PropTypes.string,
  showPhone: PropTypes.bool,
};

const FormContainer = styled.div`
  background: #f2f2f7;
  border-radius: 25px;
  padding: 1.2rem;
  min-width: 350px;
  max-width: 400px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    min-width: 280px;
    max-width: 350px;
    padding: 1rem;
    border-radius: 20px;
  }

  @media (max-width: 480px) {
    min-width: 250px;
    max-width: 300px;
    padding: 0.8rem;
    border-radius: 16px;
  }

  @media (max-width: 360px) {
    min-width: 220px;
    max-width: 280px;
  }
`;

const WelcomeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 0;
`;

const WelcomeMessage = styled.div`
  font-size: 1.1rem;
  text-align: center;
  color: #1d1d1f;
`;

const WelcomeGreeting = styled.div`
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 480px) {
    font-size: 1.2rem;
  }

  @media (max-width: 360px) {
    font-size: 1.1rem;
  }
`;

const FormTitle = styled.h2`
  color: #1d1d1f;
  margin: 0 0 18px 0;
  font-size: 2rem;
  text-align: center;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }

  @media (max-width: 360px) {
    font-size: 1.6rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const NameInputs = styled.div`
  display: flex;
  gap: 0.55rem;
  width: 100%;

  & > div {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: 360px) {
    flex-direction: column;
    gap: 0.55rem;

    & > div {
      flex: none;
    }
  }
`;

const CustomStyledInput = styled.input`
  padding: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.8);
  color: #1d1d1f;
  font-size: 1rem;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: rgba(0, 0, 0, 0.5);
  }

  &:focus {
    outline: none;
    border-color: rgba(0, 0, 0, 0.4);
    background: rgba(255, 255, 255, 0.95);
  }

  @media (max-width: 480px) {
    padding: 0.65rem;
  }
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const BottomText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  font-size: 0.5rem;

  a {
    text-decoration: underline !important;
    color: #007aff !important;
  }
`;

const TenDlcNotice = styled.div`
  color: #8e8e93;
  font-family: "Arial", sans-serif;
  text-decoration: none;
  max-width: 325px;
  text-align: center;
`;

const TenDlcCheckbox = styled.div`
  color: #8e8e93;
  font-family: "Arial", sans-serif;
  text-decoration: none;
  max-width: 325px;
  display: flex;
  align-items: center;

  input {
    margin-right: 0.2rem;
    margin-left: 0;
    transform: scale(0.8);
  }
`;

const AdminWrapper = styled.div`
  font-weight: 400;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.4rem;
  margin-top: 1rem;
`;

const AdminText = styled.div`
  opacity: 0.9;
  /* margin: 0 10px; // to avoid single word wrapping (for most font options) */
`;

const ButtonIcon = styled.span`
  margin-right: 0.5rem;
`;

export { SignUpButton };
export default GetInvolvedForm;
